import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { fmtCVE0, fmtDate } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Buildings, TrendUp, CheckCircle, Clock, CircleNotch, Gear, MagnifyingGlass, HourglassMedium, Tag, Crown } from "@phosphor-icons/react";
import { periodLabel } from "@/lib/pricing";
import { toast } from "sonner";
import { FeatureSwitches } from "@/components/platform/FeatureSwitches";

const STATE = {
  active: { label: "Ativo", cls: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  trialing: { label: "Em teste", cls: "border-blue-200 bg-blue-50 text-blue-700" },
  expired: { label: "Expirado", cls: "border-red-200 bg-red-50 text-red-700" },
};
const PLAN_NAMES = { essencial: "Essencial", pro: "Pro", empresa: "Empresa" };

function Stat({ icon: Icon, label, value, sub, tone = "default", testid }) {
  const tones = { default: "text-primary bg-accent", good: "text-emerald-700 bg-emerald-100", warn: "text-amber-700 bg-amber-100" };
  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid={testid}>
      <div className={`mb-3 flex h-10 w-10 items-center justify-center rounded-lg ${tones[tone]}`}><Icon size={20} weight="bold" /></div>
      <div className="font-display text-2xl font-extrabold tracking-tight">{value}</div>
      <div className="mt-0.5 text-sm text-muted-foreground">{label}</div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

function suggestedAmount(pricing, org, plan, months) {
  const base = pricing?.plans?.[plan]?.prices?.[String(months)];
  if (base === undefined || base === null) return "";
  const pct = org?.founder === "active" ? org.founder_pct : (!org?.founder && pricing.founder?.enabled ? pricing.founder.discount_pct : 0);
  return Math.round(base * (1 - (pct || 0) / 100));
}

function ConfirmDialog({ org, pricing, onClose, onDone }) {
  const req = org?.pending_request;
  const [f, setF] = useState({ plan: "essencial", months: 12, amount: "", reference: "", method: "transferencia" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!org) return;
    const plan = req?.plan || org.plan || "essencial";
    const months = req?.months || org.months || 12;
    setF({ plan, months, amount: req?.amount ?? suggestedAmount(pricing, org, plan, months), reference: req?.reference || "", method: "transferencia" });
  }, [org, req, pricing]);
  const setPlan = (k, v) => {
    const next = { ...f, [k]: v };
    if (!req) next.amount = suggestedAmount(pricing, org, next.plan, next.months);
    setF(next);
  };
  const submit = async () => {
    if (f.amount === "" || Number(f.amount) < 0) return toast.error("Indique o valor recebido");
    setBusy(true);
    try {
      await api.post(`/platform/organizations/${org.id}/confirm-payment`,
        { ...f, months: Number(f.months), amount: Number(f.amount), request_id: req?.id || null });
      toast.success("Pagamento confirmado e plano ativado");
      onDone();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!org} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="confirm-payment-dialog">
        <DialogHeader>
          <DialogTitle>Confirmar pagamento</DialogTitle>
          <DialogDescription>{org?.name}{req ? ` · pedido ${req.reference}` : ""}. O plano é ativado ou renovado a partir do fim do período atual.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Plano</Label>
            <Select value={f.plan} onValueChange={(v) => setPlan("plan", v)}>
              <SelectTrigger data-testid="confirm-plan"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(PLAN_NAMES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-1.5"><Label>Duração</Label>
            <Select value={String(f.months)} onValueChange={(v) => setPlan("months", Number(v))}>
              <SelectTrigger data-testid="confirm-months"><SelectValue /></SelectTrigger>
              <SelectContent>{[1, 6, 12].map((d) => <SelectItem key={d} value={String(d)}>{periodLabel(d)}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-1.5"><Label>Valor recebido (CVE)</Label>
            <Input type="number" min="0" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="confirm-amount" /></div>
          <div className="space-y-1.5"><Label>Método</Label>
            <Select value={f.method} onValueChange={(v) => setF({ ...f, method: v })}>
              <SelectTrigger data-testid="confirm-method"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="transferencia">Transferência</SelectItem><SelectItem value="vinti4">Vinti4</SelectItem><SelectItem value="numerario">Numerário</SelectItem></SelectContent>
            </Select></div>
          <div className="col-span-2 space-y-1.5"><Label>Referência</Label>
            <Input value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} data-testid="confirm-reference" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={busy} data-testid="confirm-payment-submit">{busy ? <CircleNotch className="animate-spin" /> : "Confirmar e ativar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Platform() {
  const [data, setData] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [error, setError] = useState(false);
  const [q, setQ] = useState("");
  const [target, setTarget] = useState(null);

  const load = useCallback(() => {
    api.get("/platform/overview").then((r) => setData(r.data)).catch(() => setError(true));
    api.get("/platform/pricing").then((r) => setPricing(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  if (error) return (
    <div className="flex h-64 flex-col items-center justify-center gap-3 text-center" data-testid="platform-denied">
      <Buildings size={40} className="text-muted-foreground" /><p className="text-muted-foreground">Acesso restrito ao proprietário da plataforma.</p>
    </div>
  );
  if (!data) return <div className="flex h-64 items-center justify-center"><CircleNotch size={28} className="animate-spin text-primary" /></div>;

  const t = data.totals;
  const rows = data.organizations.filter((o) => !q || `${o.name} ${o.nif || ""} ${o.email || ""}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-6" data-testid="platform-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Super-admin</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Painel GESTA CV</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="rounded-full" asChild data-testid="platform-prospect-link">
            <Link to="/plataforma/prospeccao"><MagnifyingGlass size={16} className="mr-1.5" /> Prospeção</Link>
          </Button>
          <Button variant="outline" className="rounded-full" asChild data-testid="platform-company-link">
            <Link to="/plataforma/empresa"><Buildings size={16} className="mr-1.5" /> Dados da empresa</Link>
          </Button>
          <Button variant="outline" className="rounded-full" asChild data-testid="platform-plans-link">
            <Link to="/plataforma/planos"><Tag size={16} className="mr-1.5" /> Planos e preços</Link>
          </Button>
          <Button variant="outline" className="rounded-full" asChild data-testid="platform-settings-link">
            <Link to="/plataforma/pagamentos"><Gear size={16} className="mr-1.5" /> Configurações de pagamento</Link>
          </Button>
        </div>
      </div>

      <FeatureSwitches />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Stat testid="stat-revenue" icon={TrendUp} tone="good" label="Receita total" value={fmtCVE0(data.revenue_total)} sub={`${data.payments_count} pagamento(s) confirmados`} />
        <Stat testid="stat-active" icon={CheckCircle} tone="good" label="Ativas" value={t.active}
          sub={`${data.by_plan.essencial} Essencial · ${data.by_plan.pro} Pro · ${data.by_plan.empresa} Empresa`} />
        <Stat testid="stat-trial" icon={Clock} label="Em teste" value={t.trialing} />
        <Stat testid="stat-pending" icon={HourglassMedium} tone="warn" label="Pagamentos pendentes" value={data.pending_count} />
        <Stat testid="stat-total" icon={Buildings} label="Empresas" value={t.organizations} sub={`${t.expired} expiradas · ${data.founders?.used ?? 0}/${data.founders?.max ?? 0} fundadoras`} />
      </div>

      <div className="rounded-xl border border-border bg-card" data-testid="platform-orgs">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-3">
          <span className="font-display font-bold">Empresas</span>
          <div className="relative w-full max-w-xs">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input className="h-9 pl-8" placeholder="Pesquisar empresa, NIF, email" value={q} onChange={(e) => setQ(e.target.value)} data-testid="platform-search" />
          </div>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Empresa</TableHead>
              <TableHead className="hidden lg:table-cell">Setor</TableHead>
              <TableHead>Plano</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead className="hidden md:table-cell">Vencimento</TableHead>
              <TableHead className="hidden xl:table-cell">Pedido pendente</TableHead>
              <TableHead className="text-right">Ação</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((o) => {
              const s = STATE[o.state] || STATE.expired;
              return (
                <TableRow key={o.id} className="stagger-item" data-testid={`org-row-${o.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-1.5 font-medium">{o.name}
                      {o.founder === "active" && <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-800" data-testid={`org-founder-${o.id}`}><Crown size={11} weight="fill" /> Fundador nº {o.founder_number}</span>}
                      {o.founder === "lost" && <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500" data-testid={`org-founder-lost-${o.id}`}>Fundador perdido</span>}
                    </div>
                    <div className="text-xs text-muted-foreground">{o.nif || "—"} · {o.email || "—"}{o.island ? ` · ${o.island}` : ""}</div>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell text-muted-foreground">{o.sector || "—"}</TableCell>
                  <TableCell>
                    {o.plan_name ? `${o.plan_name}${o.months ? ` · ${periodLabel(o.months)}` : ""}` : "—"}
                    {o.provider === "stripe" && <div className="text-[11px] text-muted-foreground" data-testid={`org-provider-${o.id}`}>Cartão (Stripe)</div>}
                  </TableCell>
                  <TableCell><Badge variant="outline" className={s.cls}>{s.label}</Badge></TableCell>
                  <TableCell className="hidden md:table-cell text-muted-foreground">{fmtDate(o.end_date)}</TableCell>
                  <TableCell className="hidden xl:table-cell">
                    {o.pending_request ? (
                      <span className="text-xs"><b className="font-mono">{o.pending_request.reference}</b> · {o.pending_request.plan_name} {o.pending_request.months}m</span>
                    ) : <span className="text-xs text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" className="rounded-full" variant={o.pending_request ? "default" : "outline"} onClick={() => setTarget(o)}
                      data-testid={`confirm-payment-btn-${o.id}`}>Confirmar pagamento</Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <ConfirmDialog org={target} pricing={pricing} onClose={() => setTarget(null)} onDone={() => { setTarget(null); load(); }} />
    </div>
  );
}
